/**
 * BlogPost — Public individual article page for Searchline
 * Route: /blog/:slug (no auth required)
 *
 * Fetches a single published article by slug and renders body_md as HTML.
 */

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'
import { marked } from 'marked'
import searchlineLogo from '@/assets/searchline-logo.jpg'

const supabase = createClient(
  'https://xwcmvemayjjcfyjhdkii.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh3Y212ZW1heWpqY2Z5amhka2lpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ3OTAyNzMsImV4cCI6MjA5MDM2NjI3M30.Ej0O6Lh8QI8g5VbDjlPw1aBpcuWr2qvxYZrQREL6vgo'
)

interface Article {
  id: string
  title: string
  slug: string
  summary: string
  image_url?: string
  keywords: string[]
  body_md: string
  seo_focus: string
  aeo_questions: string[]
  created_at: string
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const [article, setArticle] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) return
    async function fetchArticle() {
      const { data, error } = await supabase
        .from('radar_articles')
        .select('id, title, slug, summary, keywords, body_md, seo_focus, aeo_questions, image_url, created_at')
        .eq('slug', slug)
        .eq('status', 'published')
        .single()

      if (error || !data) {
        setNotFound(true)
      } else {
        setArticle(data)
      }
      setLoading(false)
    }
    fetchArticle()
  }, [slug])

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0f172a]/90 backdrop-blur border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={searchlineLogo} alt="Searchline" className="w-8 h-8 rounded-lg" />
            <div>
              <span className="text-white font-bold text-lg tracking-tight">Searchline</span>
              <span className="block text-[#f97316] font-medium text-xs">AI Hiring</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/blog"
              className="text-white/50 hover:text-white text-sm transition-colors"
            >
              ← Blog
            </Link>
            <Link
              to="/login"
              className="bg-[#f97316] hover:bg-[#ea6c0a] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 pb-24">
        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-40">
            <div className="w-8 h-8 border-2 border-[#f97316] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* 404 */}
        {!loading && notFound && (
          <div className="text-center py-40">
            <div className="w-20 h-20 rounded-full bg-[#f97316]/10 border border-[#f97316]/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">🔍</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Article not found</h1>
            <p className="text-white/40 mb-8">
              This article doesn't exist or isn't published yet.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-[#f97316] hover:bg-[#ea6c0a] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              ← Back to Blog
            </Link>
          </div>
        )}

        {/* Article */}
        {!loading && article && (
          <>
            {/* Back link */}
            <div className="pt-10 pb-6">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-sm transition-colors"
              >
                ← Back to Blog
              </Link>
            </div>

            {/* Article header */}
            <header className="mb-10">
              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-3 mb-5 text-sm text-white/40">
                <time dateTime={article.created_at}>
                  {new Date(article.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-5">
                {article.title}
              </h1>

              {/* Cover image — inline style, no aspect-ratio constraint, shows full image */}
              {article.image_url && (
                <div style={{ marginTop: 24, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: '#0a0f1e', lineHeight: 0 }}>
                  <img
                    src={article.image_url}
                    alt={article.title}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
              )}

              {/* Keywords */}
              {article.keywords && article.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {article.keywords.map((kw) => (
                    <span
                      key={kw}
                      className="text-xs px-3 py-1 rounded-full bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20 font-medium"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Divider */}
            <hr className="border-white/10 mb-10" />

            {/* Body — strip from any FAQ/Frequently Asked heading to end of content */}
            {article.body_md ? (
              <div
                className="prose-blog"
                dangerouslySetInnerHTML={{
                  __html: marked(
                    (() => {
                      const md = article.body_md
                      // Remove everything from a FAQ heading onwards
                      const faqIdx = md.search(/^#{1,4}\s*(FAQ|Frequently Asked)/im)
                      return (faqIdx > 0 ? md.slice(0, faqIdx) : md).trim()
                    })()
                  ) as string
                }}
              />
            ) : (
              <p className="text-white/40 italic">No content available.</p>
            )}

            {/* AEO Questions — omitted from public page (questions only, no answers = no SEO value.
                 Full FAQ with schema markup to be added post-launch when answers are authored.) */}

            {/* Footer CTA */}
            <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-[#f97316]/10 to-transparent border border-[#f97316]/20 text-center">
              <p className="text-white font-semibold text-lg mb-2">Ready to transform your hiring?</p>
              <p className="text-white/50 text-sm mb-5">See how Searchline's AI handles the heavy lifting.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-[#f97316] hover:bg-[#ea6c0a] text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                Explore Searchline →
              </Link>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-white/30 text-sm">© {new Date().getFullYear()} Searchline. All rights reserved.</span>
          <Link to="/blog" className="text-white/30 hover:text-white/60 text-sm transition-colors">
            ← Back to Blog
          </Link>
        </div>
      </footer>

      {/* Inline prose styles for blog body */}
      <style>{`
        .prose-blog {
          color: rgba(255,255,255,0.75);
          font-size: 1.0625rem;
          line-height: 1.8;
        }
        .prose-blog h1,
        .prose-blog h2,
        .prose-blog h3,
        .prose-blog h4 {
          color: #fff;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }
        .prose-blog h1 { font-size: 1.75rem; }
        .prose-blog h2 { font-size: 1.375rem; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 0.5rem; }
        .prose-blog h3 { font-size: 1.125rem; }
        .prose-blog p { margin-bottom: 1.25rem; }
        .prose-blog a { color: #f97316; text-decoration: underline; }
        .prose-blog a:hover { color: #fb923c; }
        .prose-blog strong { color: #fff; font-weight: 600; }
        .prose-blog em { color: rgba(255,255,255,0.6); font-style: italic; }
        .prose-blog ul, .prose-blog ol { margin-bottom: 1.25rem; padding-left: 1.5rem; }
        .prose-blog li { margin-bottom: 0.4rem; }
        .prose-blog ul li { list-style-type: disc; }
        .prose-blog ol li { list-style-type: decimal; }
        .prose-blog blockquote {
          border-left: 3px solid #f97316;
          padding-left: 1rem;
          margin: 1.5rem 0;
          color: rgba(255,255,255,0.5);
          font-style: italic;
        }
        .prose-blog code {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 4px;
          padding: 0.15em 0.4em;
          font-size: 0.875em;
          color: #fb923c;
          font-family: monospace;
        }
        .prose-blog pre {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 1.25rem;
          overflow-x: auto;
          margin-bottom: 1.5rem;
        }
        .prose-blog pre code {
          background: none;
          border: none;
          padding: 0;
          color: rgba(255,255,255,0.8);
        }
        .prose-blog hr {
          border-color: rgba(255,255,255,0.1);
          margin: 2rem 0;
        }
        .prose-blog table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1.5rem;
        }
        .prose-blog th, .prose-blog td {
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.6rem 0.8rem;
          text-align: left;
        }
        .prose-blog th { background: rgba(255,255,255,0.05); color: #fff; font-weight: 600; }
        .prose-blog img { max-width: 100%; border-radius: 12px; margin: 1rem 0; }
      `}</style>
    </div>
  )
}
